import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  any,
  ascend,
  compose,
  concat,
  descend,
  dropLast,
  equals,
  filter,
  groupBy,
  includes,
  indexOf,
  isEmpty,
  join,
  last,
  length,
  map,
  none,
  path,
  pipe,
  pluck,
  prop,
  propEq,
  reduce,
  reverse,
  split,
  take,
  takeLast,
  test,
  toUpper,
  values,
  without,
  zipWith,
} from "ramda";
import { deduplicate } from "../../util/mediaUtil";
import { notBlank } from "../../util/ext";
import Skeleton from "@mui/material/Skeleton";
import { useModuleMedia } from "../modules/moduleServices";
import { useAnthologyCollectionsCreator } from "../creatorServices";
import { useCollectionApprovedContent } from "../collections/collectionServices";

//-- Adding/removing children from parents requires
//-- creating updated lists of parent's previous children lists
export const childrenHooks = {
  module: useModuleMedia,
  anthology: useAnthologyCollectionsCreator,
  collection: useCollectionApprovedContent,
};

export const setIds = ({ mediaType, prevMediaList, itemsToRemove }) => {
  const updatedMediaList = notBlank(prevMediaList)
    ? without(itemsToRemove, pluck("id", prevMediaList))
    : [];
  switch (mediaType) {
    case "module":
      return itemsToRemove;
    case "anthology":
      return updatedMediaList;
    case "collection":
      return updatedMediaList;
    default:
      return itemsToRemove;
  }
};

export const createRemoveBody = ({ parent, newChildrenIds }) => {
  const ids = setIds({
    mediaType: parent.mediaType,
    prevMediaList: parent.prevMediaList,
    itemsToRemove: newChildrenIds,
  });

  return { id: parent.id, ids };
};

export const groupChildrenByParent = compose(
  map(children => {
    // all children will have same parent and prevMediaList
    const child = children[0];
    return {
      prevParent: {
        prevMediaList: child.prevMediaList,
        ...child.parent,
      },
      newChildren: { ids: map(path(["id"]), children) },
    };
  }),
  values,
  groupBy(path(["parent", "id"])),
);

const ORGANIZE_PATH = "/creator/organize";

export const buildOrganizeViewPath = ({ view, ids }) => {
  return notBlank(ids)
    ? `${ORGANIZE_PATH}?view=${view}&id=${join(",", ids)}`
    : `${ORGANIZE_PATH}?view=${view}`;
};

export const buildViewPaths = ({ views, ids }) => {
  return reverse(
    map(view => {
      const idArr = dropLast(indexOf(view, views), ids);

      const viewsArr = dropLast(indexOf(view, views), views);

      const idParam = `id=${join(",", idArr)}`;
      const viewParam = `view=${join(",", viewsArr)}`;

      return `${ORGANIZE_PATH}?${viewParam}&${idParam}`;
    }, take(length(ids), views)),
  );
};

export const buildBreadcrumbs = ({ views, ids }) => {
  const viewPaths = buildViewPaths({ views, ids });
  const groups = zipWith(
    (mediaType, id) => ({ mediaType, id }),
    takeLast(length(ids), views),
    ids,
  );

  return zipWith(
    (group, viewPath) => ({ ...group, viewPath }),
    groups,
    viewPaths,
  );
};

// These match the node ids of the OrganizeSidebar
// MUI TreeView keeps track of expanded nodes with node ids
export const OrganizeViews = {
  REVIEW: "review",
  PROCESSING: "processing",
  PENDING: "pending",
  REJECTED: "rejected",
  APPROVED: "approved",
  ORGANIZE: "organize",
  CONTENT_GROUPS: "organize-grouped",
  UNGROUPED_ITEMS: "organize-ungrouped",
  MODULE: "module",
  ANTHOLOGY: "anthology",
  COLLECTION: "collection",
  ARCHIVED: "archived",
};

export const getReviewViewPath = view =>
  buildOrganizeViewPath({ view: buildReviewViewPath(view) });

export const buildReviewViewPath = status =>
  join(",", [OrganizeViews.REVIEW, OrganizeViews[toUpper(status)]]);

export const getReviewHome = () => buildReviewViewPath(OrganizeViews.PENDING);

// Use below if top node in sidebar should direct to home screen
// export const getContentGroupsViewPath = () =>
//   buildOrganizeViewPath({
//     view: OrganizeViews.ORGANIZE,
//   });

export const CONTENT_GROUPS_VIEW = join(",", [
  OrganizeViews.ORGANIZE,
  OrganizeViews.CONTENT_GROUPS,
]);
export const getContentGroupsViewPath = () =>
  buildOrganizeViewPath({
    view: CONTENT_GROUPS_VIEW,
  });

export const UNGROUPED_ITEMS_VIEW = join(",", [
  OrganizeViews.ORGANIZE,
  OrganizeViews.UNGROUPED_ITEMS,
]);

export const getUngroupedItemsViewPath = () =>
  buildOrganizeViewPath({
    view: UNGROUPED_ITEMS_VIEW,
  });

export const MODULE_VIEW = join(",", [
  OrganizeViews.ORGANIZE,
  OrganizeViews.CONTENT_GROUPS,
  OrganizeViews.MODULE,
]);

export const getModuleViewPath = id =>
  buildOrganizeViewPath({ view: MODULE_VIEW, ids: [id] });

export const buildModuleChildView = childMediaType =>
  join(",", [
    OrganizeViews.ORGANIZE,
    OrganizeViews.CONTENT_GROUPS,
    OrganizeViews.MODULE,
    OrganizeViews[childMediaType],
  ]);

export const COLLECTION_VIEW = join(",", [
  OrganizeViews.ORGANIZE,
  OrganizeViews.CONTENT_GROUPS,
  OrganizeViews.MODULE,
  OrganizeViews.ANTHOLOGY,
  OrganizeViews.COLLECTION,
]);

export const UNGROUPED_COLLECTION_VIEW = join(",", [
  OrganizeViews.ORGANIZE,
  OrganizeViews.UNGROUPED_ITEMS,
  OrganizeViews.COLLECTION,
]);

export const UNGROUPED_ANTHOLOGY_VIEW = join(",", [
  OrganizeViews.ORGANIZE,
  OrganizeViews.UNGROUPED_ITEMS,
  OrganizeViews.ANTHOLOGY,
]);

export const UNGROUPED_ANTHOLOGY_CHILD_VIEW = join(",", [
  OrganizeViews.ORGANIZE,
  OrganizeViews.UNGROUPED_ITEMS,
  OrganizeViews.ANTHOLOGY,
  OrganizeViews.COLLECTION,
]);

export const buildExpandedId = ({ views, ids }) => {
  return reverse(
    map(view => {
      const idArr = dropLast(indexOf(view, views), ids);

      const viewsArr = dropLast(indexOf(view, views), views);

      if (isEmpty(idArr)) {
        return last(viewsArr);
      }

      return join("-", concat(viewsArr, idArr));
    }, views),
  );
};

const ONLY_MOVE = ["anthology"];

const ONLY_MOVE_ALLOWED_VIEWS = [
  OrganizeViews.ANTHOLOGY,
  OrganizeViews.UNGROUPED_ITEMS,
];

const ACCEPT_MOVE_OR_COPY_DRAGGABLES = [
  "module",
  "anthology",
  "collection",
  "ungrouped-collection",
];

const NOT_CONTENT_VIEWS = [
  "module",
  "anthology",
  "collection",
  "ungrouped-collection",
  "ungrouped-collection-group",
];

const REVIEW_VIEWS = [
  OrganizeViews.REJECTED,
  OrganizeViews.PENDING,
  OrganizeViews.APPROVED,
];

const ANTHOLOGY_OR_COLLECTION = ["anthology", "collection"];

const CONTENT_GROUPS_VIEWS = [
  OrganizeViews.ORGANIZE,
  OrganizeViews.CONTENT_GROUPS,
  OrganizeViews.UNGROUPED_ITEMS,
  OrganizeViews.MODULE,
  OrganizeViews.ANTHOLOGY,
  OrganizeViews.COLLECTION,
];

const CONTENT_GROUPS = [
  OrganizeViews.MODULE,
  OrganizeViews.ANTHOLOGY,
  OrganizeViews.COLLECTION,
];

export const useSearchParam = () => {
  const { search } = useLocation();

  return useMemo(() => new URLSearchParams(search), [search]);
};

export const contentStatus = {
  pending: "pending",
  rejected: "rejected",
  approved: "approved",
};

export const otherParams = {
  all: [],
  approved: [{ cas: "APPROVED" }],
  review: [{ cas: "PENDING OR REJECTED" }],
  pending: [{ cas: "PENDING" }],
  rejected: [{ cas: "REJECTED" }],
  "ungrouped-content": [
    {
      "-pa": "*",
      "-mod": "*",
    },
  ],
};

export const acceptsMoveOrCopy = view => {
  return any(equals(view), ACCEPT_MOVE_OR_COPY_DRAGGABLES);
};

export const acceptsOnlyMove = view => {
  return any(equals(view), ONLY_MOVE);
};

export const allowCopy = view => none(equals(view), ONLY_MOVE_ALLOWED_VIEWS);

export const notContent = view => {
  return any(equals(view), NOT_CONTENT_VIEWS);
};

export const isModule = media => propEq("mediaType", "module", media);

export const isAnthology = media => propEq("mediaType", "anthology", media);

export const isCollection = media => propEq("mediaType", "collection", media);

export const isContent = media => propEq("mediaType", "content", media);

export const isAnthologyOrCollection = media => {
  return any(equals(media.mediaType), ANTHOLOGY_OR_COLLECTION);
};

export const isUngrouped = view => test(/ungrouped/, view);

export const isContentGroupsHome = view =>
  equals(view, OrganizeViews.CONTENT_GROUPS);

export const isUngroupedHome = view =>
  equals(view, OrganizeViews.UNGROUPED_ITEMS);

export const isContentGroupTopLevelView = view =>
  includes(view, [OrganizeViews.CONTENT_GROUPS, OrganizeViews.UNGROUPED_ITEMS]);

export const isContentGroupView = view =>
  any(equals(view), CONTENT_GROUPS_VIEWS);

export const isAnyReview = view => any(equals(view), REVIEW_VIEWS);

export const isPending = view => equals(view, OrganizeViews.PENDING);

export const isApproved = view => equals(view, OrganizeViews.APPROVED);

export const isRejected = view => equals(view, OrganizeViews.REJECTED);

export const isProcessing = view => equals(view, OrganizeViews.PROCESSING);

export const isApprovedOrPending = view =>
  includes(view, [OrganizeViews.APPROVED, OrganizeViews.PENDING]);

export const isPendingOrRejected = view =>
  includes(view, [OrganizeViews.PENDING, OrganizeViews.REJECTED]);

export const isApprovedOrRejected = view =>
  includes(view, [OrganizeViews.APPROVED, OrganizeViews.REJECTED]);

export const isContentGroup = view => any(equals(view), CONTENT_GROUPS);

export const isModuleView = view => equals(view, OrganizeViews.MODULE);

export const isAnthologyView = view => equals(view, OrganizeViews.ANTHOLOGY);

export const isCollectionView = view => equals(view, OrganizeViews.COLLECTION);

export const isCollectionOrAnthologyView = view =>
  includes(view, [OrganizeViews.ANTHOLOGY, OrganizeViews.COLLECTION]);

export const byRecentlyUpdated = descend(prop("auditUpdated"));

export const byNameAsc = ascend(prop("name"));

export const byNameDesc = descend(prop("name"));

export const showSkeletons = skeletonCount => {
  const skeletons = [];
  for (let i = 0; i < skeletonCount; i++) {
    skeletons.push(
      <Skeleton key={i} height="50px" style={{ margin: "0 10px" }} />,
    );
  }
  return skeletons;
};

export const getCollections = filter(propEq("mediaType", "collection"));

export const getAnthologies = filter(propEq("mediaType", "anthology"));

export const useView = () => {
  const searchParam = useSearchParam();

  const [view, setView] = useState("");
  useEffect(() => {
    const views = searchParam.get("view");
    if (notBlank(views)) {
      const view = last(split(",", views));
      setView(view);
    }
  }, [searchParam]);

  return view;
};

export const useViews = () => {
  const searchParam = useSearchParam();

  const [views, setViews] = useState("");
  useEffect(() => {
    const views = searchParam.get("view");
    setViews(split(",", views));
  }, [searchParam]);

  return views;
};

export const useIds = () => {
  const searchParam = useSearchParam();

  const [ids, setIds] = useState([]);
  useEffect(() => {
    const ids = searchParam.get("id");
    if (notBlank(ids)) {
      setIds(split(",", ids));
    }
  }, [searchParam]);

  return ids;
};

export const IncludeDialogTypes = {
  contentGroup: "contentGroup",
  content: "content",
};

export const moveNotAllowed = ({ activeType, group }) => {
  if (equals(activeType, "content")) {
    return equals(group.mediaType, "anthology");
  }
};

export //-- Content cannot be directly in an anthology.
//-- To remove content from an anthology, you must remove content from its parent collection
//-- Find collections that belong to an anthology based on a list of content found in that anthology
const getContentParentsForAnthology = (content, anthology) =>
  pipe(
    reduce(
      (acc, content) =>
        concat(
          acc,
          filter(
            collection => equals(collection.anthology.id, anthology.id),
            content.anthologyCollections,
          ),
        ),
      [],
    ),
    deduplicate,
  )(content);
